/**
 * SharedCartWebSocketService
 *
 * Lightweight table-channel websocket. Events do not carry cart state; the
 * store refetches the consolidated shared cart and lets the backend remain the
 * pricing source of truth.
 *
 * WS URL: wss://{host}/owa/shared-cart-sessions/ws/{storeId}_{tableId}
 */

var MAX_RECONNECT_ATTEMPTS = 5
var BASE_RECONNECT_DELAY_MS = 1000
var MAX_RECONNECT_DELAY_MS = 30000
var POLL_INTERVAL_MS = 5000
var CART_DEBOUNCE_MS = 500

export function SharedCartWebSocketService(options) {
  this.storeId = options.storeId
  this.tableId = options.tableId
  this.store = options.store
  this.router = options.router
  this.handlers = options.handlers || {}

  this.ws = null
  this.reconnectAttempts = 0
  this._intentionalClose = false
  this._reconnectTimer = null
  this._pollTimer = null
  this._cartDebounceTimer = null
  this._onVisible = null
}

SharedCartWebSocketService.prototype.connect = function() {
  this._registerVisibilityListener()

  if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
    return
  }

  var wsBase = this._buildWsBase()
  if (!wsBase || !this.storeId || !this.tableId) {
    console.warn('[SharedCartWS] Cannot determine WS URL — skipping connect')
    return
  }

  var channelKey = this.storeId + '_' + this.tableId
  var url = wsBase + '/shared-cart-sessions/ws/' + encodeURIComponent(channelKey)
  var self = this

  try {
    this.ws = new WebSocket(url)
  } catch (e) {
    console.error('[SharedCartWS] Failed to create WebSocket:', e)
    this._scheduleReconnect()
    return
  }

  this.ws.onopen = function() {
    self.reconnectAttempts = 0
    self._stopPolling()
    self.store.commit('sharedCart/SET_WS_CONNECTED', true)
  }

  this.ws.onmessage = function(event) {
    var data
    try {
      data = JSON.parse(event.data)
    } catch (e) {
      console.warn('[SharedCartWS] Non-JSON message received:', event.data)
      return
    }
    self._handleEvent(data)
  }

  this.ws.onclose = function() {
    self.store.commit('sharedCart/SET_WS_CONNECTED', false)
    if (!self._intentionalClose) {
      self._startPolling()
      self._scheduleReconnect()
    }
  }

  this.ws.onerror = function(event) {
    console.error('[SharedCartWS] WebSocket error:', event)
    if (self.handlers.onError) self.handlers.onError(event)
  }
}

SharedCartWebSocketService.prototype.disconnect = function() {
  this._intentionalClose = true
  this._stopPolling()
  this._clearReconnectTimer()
  this._clearCartDebounceTimer()
  this._unregisterVisibilityListener()
  if (this.ws) {
    this.ws.close()
    this.ws = null
  }
  this.store.commit('sharedCart/SET_WS_CONNECTED', false)
}

SharedCartWebSocketService.prototype._handleEvent = function(data) {
  var eventType = data.eventType
  var sessionId = data.sessionId

  if (sessionId) {
    this.store.commit('sharedCart/SET_SESSION_ID', sessionId)
  }

  if (eventType === 'CART_ITEMS_CHANGED') {
    this._debouncedRefetch()
  } else if (eventType === 'ROUND_FIRED') {
    this._clearCartDebounceTimer()
    this.store.dispatch('sharedCart/refetchAndMerge')
    // Skip the snack while this client's own finalize is in flight — finalize
    // fires the remaining round itself, so the echoed ROUND_FIRED event would
    // just flash "Items sent to the kitchen" right before the close-table
    // dialog, which the finalize-confirm copy already told the host about.
    if (!this.store.state.sharedCart.isLoading) {
      var roundFiredText = (this.store._vm && this.store._vm.$vuetify)
        ? this.store._vm.$vuetify.t('$vuetify.sharedCart.roundFired')
        : 'Items sent to the kitchen.'
      this.store.dispatch('showSnack', { text: roundFiredText, color: 'primary', timeout: 3000 })
    }
  } else if (eventType === 'COUPON_CHANGED') {
    this._clearCartDebounceTimer()
    this.store.dispatch('sharedCart/refetchAndMerge')
  } else if (eventType === 'SESSION_COMPLETED') {
    this.disconnect()
    // SESSION_COMPLETED is itself the authoritative "this session is over" signal --
    // don't gate showing the dialog on order.orderStatus reaching a terminal value via
    // a follow-up refetch. Some flows (e.g. pay-at-counter/cash closeouts) never flip
    // the nested order's orderStatus away from UNPAID even after the table is genuinely
    // closed out in POS, so a refetch-and-check-orderStatus gate silently never fires --
    // regression: the "Table Paid" dialog stopped showing entirely for those stores.
    // Read whatever orderStatus IS on hand (from the last known state.order, refreshed
    // moments ago by the CART_ITEMS_CHANGED/ROUND_FIRED debounce) only to pick which
    // copy variant to show (paid vs pay-at-register) -- never to decide WHETHER to show
    // it, since this event already answered that question. But only trust orderStatus
    // when it's ALREADY one of the values App.vue's sessionClosedNotice watcher actually
    // recognizes (PAY_REQUEST/PAID/COMPLETED) -- a realistic value like 'UNPAID' (the
    // pay-at-counter/cash-closeout case this whole fix is about) is truthy, so `||` alone
    // would never fall back and the watcher would show neither dialog. SESSION_COMPLETED
    // itself already proved the table is done; default to 'COMPLETED' for anything the
    // watcher wouldn't otherwise recognize as terminal.
    var TERMINAL_ORDER_STATUSES = ['PAY_REQUEST', 'PAID', 'COMPLETED']
    var lastKnownOrderStatus = this.store.state.sharedCart.order && this.store.state.sharedCart.order.orderStatus
    var noticeOrderStatus = TERMINAL_ORDER_STATUSES.indexOf(lastKnownOrderStatus) !== -1
      ? lastKnownOrderStatus
      : 'COMPLETED'
    // Clear first -- dispatch resolves on a microtask even though clearSharedCart's own
    // body is synchronous, so chaining the notice off its resolution (not committing it
    // beforehand) is what actually guarantees the clear (in particular
    // clearPersistedSession's localStorage wipe) has landed before the dialog shows,
    // matching the ordering that already fixed the refresh-race for the
    // orderStatus-driven path this mirrors.
    var self = this
    // Remember it's over before clearing wipes the id -- see startSession's notify-once guard.
    this.store.dispatch('sharedCart/markSessionDead', this.store.state.sharedCart.sessionId)
    this.store.dispatch('sharedCart/clearSharedCart').catch(function() {}).then(function() {
      self.store.commit('sharedCart/SET_SESSION_CLOSED_NOTICE', { orderStatus: noticeOrderStatus })
      if (self.handlers.onCompleted) self.handlers.onCompleted()
    })
  } else if (eventType === 'SESSION_CANCELLED') {
    this.disconnect()
    this.store.dispatch('sharedCart/handleCancelled')
    if (this.handlers.onCancelled) this.handlers.onCancelled()
  } else {
    console.warn('[SharedCartWS] Unknown event type:', eventType)
  }
}

SharedCartWebSocketService.prototype._debouncedRefetch = function() {
  var self = this
  this._clearCartDebounceTimer()
  this._cartDebounceTimer = setTimeout(function() {
    self.store.dispatch('sharedCart/refetchAndMerge')
  }, CART_DEBOUNCE_MS)
}

SharedCartWebSocketService.prototype._scheduleReconnect = function() {
  if (this._intentionalClose) return
  if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    console.warn('[SharedCartWS] Max reconnect attempts reached. Staying in poll-only mode.')
    return
  }

  var delay = Math.min(
    BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts),
    MAX_RECONNECT_DELAY_MS
  )
  var self = this
  this._clearReconnectTimer()
  this._reconnectTimer = setTimeout(function() {
    self.reconnectAttempts++
    self.connect()
  }, delay)
}

SharedCartWebSocketService.prototype._clearReconnectTimer = function() {
  if (this._reconnectTimer) {
    clearTimeout(this._reconnectTimer)
    this._reconnectTimer = null
  }
}

SharedCartWebSocketService.prototype._clearCartDebounceTimer = function() {
  if (this._cartDebounceTimer) {
    clearTimeout(this._cartDebounceTimer)
    this._cartDebounceTimer = null
  }
}

SharedCartWebSocketService.prototype._startPolling = function() {
  if (this._pollTimer) return
  var self = this
  this._pollTimer = setInterval(function() {
    self.store.dispatch('sharedCart/refetchAndMerge').catch(function() {})
  }, POLL_INTERVAL_MS)
}

SharedCartWebSocketService.prototype._stopPolling = function() {
  if (this._pollTimer) {
    clearInterval(this._pollTimer)
    this._pollTimer = null
  }
}

SharedCartWebSocketService.prototype._buildWsBase = function() {
  var apiBase = process.env.VUE_APP_API || ''
  if (apiBase) {
    return apiBase.replace(/^http/, 'ws').replace(/\/+$/, '')
  }
  if (typeof window !== 'undefined') {
    var protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    return protocol + '//' + window.location.host
  }
  return null
}

// Mobile Safari/Chrome routinely suspend a backgrounded tab's WebSocket
// without ever firing onclose — the reconnect/polling fallback below only
// starts inside onclose, so a locked/backgrounded phone can sit with
// wsConnected: true on a dead socket indefinitely, silently missing
// ROUND_FIRED/SESSION_COMPLETED pushes. On resume, verify the socket is
// actually open; if not, reconnect immediately (don't wait out the
// exponential backoff) and refetch once to catch up on anything missed.
SharedCartWebSocketService.prototype._registerVisibilityListener = function() {
  if (this._onVisible || typeof document === 'undefined' || !document.addEventListener) return
  var self = this
  this._onVisible = function() {
    if (document.visibilityState !== 'visible') return
    if (self._intentionalClose) return
    if (!self.ws || self.ws.readyState !== WebSocket.OPEN) {
      self.reconnectAttempts = 0
      self.connect()
    }
    self.store.dispatch('sharedCart/refetchAndMerge').catch(function() {})
  }
  document.addEventListener('visibilitychange', this._onVisible)
}

SharedCartWebSocketService.prototype._unregisterVisibilityListener = function() {
  if (this._onVisible && typeof document !== 'undefined' && document.removeEventListener) {
    document.removeEventListener('visibilitychange', this._onVisible)
  }
  this._onVisible = null
}
